import React, { Component } from 'react';
import moment from 'moment';
import SelectInput from './SelectInput';
import TextInput, { displayErrors } from './TextInput';
import { capitalize, stripNonNumeric } from '../../core/util/string';
import { keypressToMonth, standardizeYear } from '../../core/util/date';
import { isEmptyObject } from '../../core/util/object';
import { isClientSide } from '../../core/util/system';
import { emptyFunction } from '../../core/util/function';
import { normalizeMonthFast } from '../SignUpForm/utils';
import { EN, SupportedLocale } from '../../core/util/localizedStr';

const DATE_KEY_DAY = 'd';
const DATE_KEY_MONTH = 'm';
const DATE_KEY_YEAR = 'y';

const formatMonth = (m: any, f = 'MM', locale: SupportedLocale) =>
  capitalize(moment().locale(locale).month(m).format(f), { lowercaseRest: true, locale });

const getYear = (value: any) => value.substring(0, 4);
const getMonth = (value: any) => value.substring(5, 7);
const getDay = (value: any) => value.substring(8);

const getMonths = (locale: SupportedLocale) =>
  // @ts-expect-error ts-migrate(2345) FIXME: Type '{ length: number; }' is missing the followin... Remove this comment to see the full error message
  Array.apply(null, { length: 12 }).map((_, i) => ({
    key: i,
    value: formatMonth(i, 'MMMM', EN),
    label: formatMonth(i, 'MMMM', locale),
  }));

const getDays = () =>
  // @ts-expect-error ts-migrate(2345) FIXME: Type '{ length: number; }' is missing the followin... Remove this comment to see the full error message
  Array.apply(null, { length: 31 }).map((_, i) => ({
    key: i + 1,
    value: i + 1,
    label: i + 1,
  }));

type OwnBirthDateInputProps = {
  field: any;
  s: any;
  id: string;
  label?: string;
  showErrors?: boolean;
  trackEvent?: string;
  sensitive?: boolean;
  errorInline?: boolean;
  funPlaceholder?: boolean;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  placeholderContainerName?: string;
  submitted?: boolean;
  validateField?: (...args: any[]) => any;
  inputProps?: any;
  locale?: SupportedLocale;
};

type BirthDateInputState = any;

type BirthDateInputProps = OwnBirthDateInputProps & typeof BirthDateInput.defaultProps;

class BirthDateInput extends Component<BirthDateInputProps, BirthDateInputState> {
  static defaultProps = {
    showErrors: true,
    dayPlaceholder: 'Day',
    monthPlaceholder: 'Month',
    yearPlaceholder: 'Year',
    validateField: emptyFunction,
    locale: EN as SupportedLocale,
  };

  listener: any;

  constructor(props: BirthDateInputProps) {
    super(props);

    const stateObj = {
      year: null,
      month: null,
      day: null,
      touched: false,
    };

    if (this.props.field.initialValue) {
      stateObj.year = getYear(this.props.field.initialValue);
      stateObj.month = getMonth(this.props.field.initialValue);
      stateObj.day = getDay(this.props.field.initialValue);
    }

    this.state = stateObj;
  }

  registerKeyListener = () => {
    if (this.listener) window.removeEventListener('keydown', this.listener);

    this.listener = keypressToMonth((m: any) => {
      const { year, month, day } = this.state;

      let updatedMonth = normalizeMonthFast(m);
      if (month === '01') {
        switch (updatedMonth) {
          case '01':
            updatedMonth = '11';
            break;
          case '02':
            updatedMonth = '12';
            break;
          default:
            break;
        }
      }

      this.setState({ month: updatedMonth });
      const newDate = `${year || 'YYYY'}-${updatedMonth}-${day || 'DD'}`;
      return this.props.field.onChange(newDate);
    }, 'booking-widget');
    window.addEventListener('keydown', this.listener);
  };

  unregisterKeyListener = (cb: any) => {
    window.removeEventListener('keydown', this.listener);
    typeof cb === 'function' && cb();
  };

  render = () => {
    const { id, s, field, errorInline, submitted, showErrors } = this.props;
    const { touched } = this.state;
    const value = (this.props.inputProps && this.props.inputProps.value) || field.value;

    const getValue = (f: any) => {
      switch (f) {
        case DATE_KEY_DAY:
          return !isEmptyObject(value) ? parseInt(getDay(value), 10) : '';
        case DATE_KEY_YEAR: {
          const year = !isEmptyObject(value) ? parseInt(getYear(value), 10) : '';
          return Number.isNaN(year) || year === 0 ? '' : year;
        }

        case DATE_KEY_MONTH: {
          const month = !isEmptyObject(value) ? parseInt(getMonth(value), 10) - 1 : null;
          return month || month === 0 ? month : '';
        }

        default:
          return '';
      }
    };

    const emulateField = (dateKey: any) => ({
      ...this.props.field,

      active:
        isClientSide() &&
        document &&
        document.activeElement &&
        document.activeElement.id === `${id}-${dateKey}`,

      onChange: (e: any) => {
        let { year, month, day } = this.state;
        switch (dateKey) {
          case DATE_KEY_YEAR:
            year = `0000${stripNonNumeric(String(e.target.value))}`;
            year = year.slice(-4);
            this.setState({ year });
            break;
          case DATE_KEY_MONTH:
            month = parseInt(e.target.value, 10) + 1; // moment zero indexes months
            month = `00${String(month)}`;
            month = month.slice(-2);
            this.setState({ month });
            break;
          case DATE_KEY_DAY:
            day = `00${String(e.target.value)}`;
            day = day.slice(-2);
            this.setState({ day });
            break;
          default:
            break;
        }

        const newDate = `${year || 'YYYY'}-${month || 'MM'}-${day || 'DD'}`;
        return field.onChange(newDate);
      },

      value: getValue(dateKey),
      onBlur: () => {},
    });

    const handleOnBlur = () => {
      const { month, day } = this.state;
      this.setState({ touched: true });
      const year = standardizeYear(getValue(DATE_KEY_YEAR));
      const newDate = `${year || 'YYYY'}-${month || 'MM'}-${day || 'DD'}`;
      field.onChange(newDate);
      this.props.validateField && this.props.validateField();
    };

    const isDefaultMonth = () => getValue(DATE_KEY_MONTH) === '';
    // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
    const isDefaultDay = () => getValue(DATE_KEY_DAY) === '' || getValue(DATE_KEY_DAY) === '0';
    const getDefault = () =>
      errorInline && (touched || submitted) && field.error ? s.formFieldInlineError : s.default;

    return (
      <div className={s.formField}>
        {this.props.label && <label htmlFor={id}>{this.props.label}</label>}

        <div className={s.birthDate}>
          <div className={isDefaultMonth() ? getDefault() : ''}>
            <SelectInput
              defaultValue={this.props.monthPlaceholder}
              errorInline={this.props.errorInline}
              field={emulateField(DATE_KEY_MONTH)}
              funPlaceholder={this.props.funPlaceholder}
              id={`${id}-${DATE_KEY_MONTH}`}
              onBlur={this.unregisterKeyListener}
              onFocus={this.registerKeyListener}
              placeholder={this.props.monthPlaceholder}
              s={s}
              sensitive={this.props.sensitive}
              showErrors={false}
              submitted={this.props.submitted}
              trackEvent={this.props.trackEvent}
              values={getMonths(this.props.locale)}
            />
          </div>

          <div className={isDefaultDay() ? getDefault() : ''}>
            <SelectInput
              defaultValue={this.props.dayPlaceholder}
              errorInline={this.props.errorInline}
              field={emulateField(DATE_KEY_DAY)}
              funPlaceholder={this.props.funPlaceholder}
              id={`${id}-${DATE_KEY_DAY}`}
              placeholder={this.props.dayPlaceholder}
              s={s}
              sensitive={this.props.sensitive}
              showErrors={false}
              submitted={this.props.submitted}
              trackEvent={this.props.trackEvent}
              values={getDays()}
            />
          </div>

          <div>
            <TextInput
              errorInline={this.props.errorInline}
              field={emulateField(DATE_KEY_YEAR)}
              funPlaceholder={this.props.funPlaceholder}
              id={`${id}-${DATE_KEY_YEAR}`}
              maxLength="4"
              onBlur={handleOnBlur}
              placeholder={this.props.yearPlaceholder}
              placeholderContainerName={this.props.placeholderContainerName}
              s={s}
              sensitive={this.props.sensitive}
              showErrors={false}
              submitted={this.props.submitted}
              trackEvent={this.props.trackEvent}
              type="tel"
            />
          </div>
        </div>
        {displayErrors(field, showErrors, touched, submitted) && (
          <div className={this.props.s.formFieldError}>{this.props.field.error}</div>
        )}
      </div>
    );
  };
}

export default BirthDateInput;
